import type { ResolvedServiceFront, ServiceFrontInput, ServiceFrontStatus } from "../../domain/rdo/service-front-rules.js";
import { sessionRequestContext } from "../auth/request-context.js";
import { ContextLoadTracker, CONTEXT_LOAD_STYLES } from "../context/context-load.js";
import { changeCascade, EMPTY_CASCADE, LatestRequestGate, projectsForCascade, type CascadeSelection } from "../context/client-cascade.js";

interface ClientOption { id: string; name: string }
interface ProjectOption { id: string; code: string; name: string; clientId: string }

/** Rascunho do formulário: a Frente e o Cliente escolhido para ela. */
type FrontDraft = ServiceFrontInput & { id?: string; clientId: string };

/**
 * GESTÃO → FRENTES DE SERVIÇO.
 *
 * CLIENTE → PROJETO → FRENTE. O cabeçalho global não tem Cliente: a tela tem
 * o seu, no filtro e no formulário. O Cliente da Frente é o do Projeto, e o
 * serviço recusa Projeto de outro Cliente. Na edição o Cliente é fixo. Frente
 * com apontamento de RDO registrado não é excluída — é inativada.
 */
export class ManagementServiceFrontsPage extends HTMLElement {
  private fronts: ResolvedServiceFront[] = [];
  private clients: ClientOption[] = [];
  /** Projetos de todos os Clientes autorizados — a cascata filtra por Cliente. */
  private projects: ProjectOption[] = [];
  private usageByFront = new Map<string, number>();

  /** Filtro local CLIENTE → PROJETO (só esses dois níveis se aplicam à Frente). */
  private filter: CascadeSelection = EMPTY_CASCADE;
  private showInactive = true;
  private error: string | null = null;
  private editing: FrontDraft | null = null;

  private readonly ctxLoad = new ContextLoadTracker();
  /** A consulta de A que responder depois da de B não desenha por cima de B. */
  private readonly listGate = new LatestRequestGate();

  async connectedCallback() {
    await this.loadReferenceData();
    await this.load();
  }

  private get platform() { return window.app?.platform; }
  private get service() { return this.platform?.serviceFrontService; }

  /** Contexto da chamada com o Cliente LOCAL; vazio = Todos os Clientes autorizados. */
  private ctxFor(clientId: string) {
    return { ...sessionRequestContext(), activeClientId: clientId || undefined };
  }

  private get authorizedClientIds(): ReadonlySet<string> {
    return new Set(this.clients.map(c => c.id));
  }

  private errorText(err: unknown, fallback: string): string {
    if (err && typeof err === "object" && "safeMessage" in err && typeof err.safeMessage === "string" && err.safeMessage) return err.safeMessage;
    return err instanceof Error && err.message ? err.message : fallback;
  }

  /** Clientes autorizados e Projetos — dados de referência da cascata. */
  private async loadReferenceData() {
    const p = this.platform;
    if (!p) return;
    const ctx = this.ctxFor("");
    const clients = await this.ctxLoad.load("Clientes", () => p.adminService.listAuthorizedClients(ctx));
    this.clients = clients.map(c => ({ id: String(c.id), name: c.name || c.legalName || String(c.id) }));
    const projects = await this.ctxLoad.load("Projetos", () => p.projectService.listProjects(undefined, undefined, ctx));
    this.projects = projects.map(pr => ({ id: String(pr.id), code: pr.code || "", name: pr.name || "", clientId: String(pr.clientId || "") }));
  }

  private async load() {
    const ticket = this.listGate.begin();
    this.error = null;
    try {
      if (this.service) {
        // Cliente no contexto, Projeto no filtro: o SERVIÇO devolve só as
        // Frentes desse recorte ("Todos" = Clientes autorizados).
        const fronts: ResolvedServiceFront[] = await this.service.listFronts(this.ctxFor(this.filter.clientId), {
          includeInactive: this.showInactive,
          ...(this.filter.projectId ? { projectId: this.filter.projectId } : {})
        });
        const usage = new Map<string, number>();
        for (const front of fronts) {
          usage.set(front.id, await this.service.countUsage(this.ctxFor(String(front.clientId)), front.id));
        }
        if (!this.listGate.isCurrent(ticket)) return;
        this.fronts = fronts;
        this.usageByFront = usage;
      }
    } catch (e) {
      if (!this.listGate.isCurrent(ticket)) return;
      console.error("[FRENTES] Falha ao carregar", e);
      this.error = this.errorText(e, "Não foi possível carregar as Frentes de Serviço.");
    }
    this.render();
  }

  private esc(value: unknown): string {
    return String(value ?? "").replace(/[&<>"']/g, c =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] || c)
    );
  }

  private val(id: string): string {
    const el = this.querySelector<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(`#${id}`);
    return el ? el.value.trim() : "";
  }

  private brDate(iso: string | null): string {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? "");
    return match ? `${match[3]}/${match[2]}/${match[1]}` : "—";
  }

  /** Guarda o que já foi digitado antes de redesenhar o formulário (a cascata redesenha). */
  private captureDraft(): void {
    if (!this.editing) return;
    this.editing = {
      ...this.editing,
      code: this.val("front-code"),
      name: this.val("front-name"),
      description: this.val("front-description"),
      startDate: this.val("front-start") || null,
      endDate: this.val("front-end") || null,
      notes: this.val("front-notes"),
      status: (this.val("front-status") || "ACTIVE") as ServiceFrontStatus
    };
  }

  private async save() {
    if (!this.editing) return;
    this.captureDraft();
    const draft = this.editing;
    this.error = null;

    if (!draft.clientId) {
      this.error = "Selecione o Cliente da Frente de Serviço.";
      this.render();
      return;
    }
    if (!draft.projectId) {
      this.error = "Selecione o Projeto da Frente de Serviço.";
      this.render();
      return;
    }

    const input: ServiceFrontInput = {
      projectId: draft.projectId,
      code: draft.code,
      name: draft.name,
      description: draft.description ?? null,
      startDate: draft.startDate ?? null,
      endDate: draft.endDate ?? null,
      notes: draft.notes ?? null,
      status: draft.status ?? "ACTIVE"
    };

    try {
      // O Cliente vai no contexto e o serviço confere Projeto × Cliente.
      const ctx = this.ctxFor(draft.clientId);
      if (draft.id) {
        await this.service.updateFront(ctx, draft.id, input);
      } else {
        await this.service.createFront(ctx, input);
      }
      this.editing = null;
      await this.load();
    } catch (e) {
      console.error("[FRENTES] Falha ao salvar", e);
      this.error = this.errorText(e, "Não foi possível salvar a Frente de Serviço.");
      this.render();
    }
  }

  private frontClientCtx(id: string) {
    const front = this.fronts.find(f => f.id === id);
    return this.ctxFor(front ? String(front.clientId) : this.filter.clientId);
  }

  private async toggleStatus(id: string, current: string) {
    try {
      await this.service.setStatus(this.frontClientCtx(id), id, current === "ACTIVE" ? "INACTIVE" : "ACTIVE");
      await this.load();
    } catch (e) {
      console.error("[FRENTES] Falha ao alterar status", e);
      this.error = this.errorText(e, "Não foi possível alterar o status da Frente.");
      this.render();
    }
  }

  private async removeFront(id: string) {
    const front = this.fronts.find(f => f.id === id);
    if (!confirm(`Excluir a Frente de Serviço "${front?.name ?? id}"?`)) return;
    try {
      await this.service.deleteFront(this.frontClientCtx(id), id);
      await this.load();
    } catch (e) {
      console.error("[FRENTES] Falha ao excluir", e);
      this.error = this.errorText(e, "Não foi possível excluir a Frente de Serviço.");
      this.render();
    }
  }

  private clientNameOf(clientId: string): string {
    return this.clients.find(c => c.id === clientId)?.name ?? clientId;
  }

  private renderForm(): string {
    const draft = this.editing!;
    const locked = !!draft.id;
    const projects = draft.clientId ? projectsForCascade(this.projects, draft, this.authorizedClientIds) : [];
    const keepProject = draft.projectId && !projects.some(p => p.id === draft.projectId)
      ? `<option value="${this.esc(draft.projectId)}" selected>Projeto atual da Frente</option>`
      : "";
    return `
      <div class="front-form">
        <div class="form-title">${locked ? "Editar Frente de Serviço" : "Nova Frente de Serviço"}</div>
        <div class="form-grid">
          <div class="fld">
            <label>Cliente *</label>
            ${locked
              ? `<select id="front-client" disabled title="O Cliente de uma Frente cadastrada não é alterado."><option value="${this.esc(draft.clientId)}" selected>${this.esc(this.clientNameOf(draft.clientId))}</option></select>`
              : `<select id="front-client">
                   <option value="">Selecione o Cliente…</option>
                   ${this.clients.map(c => `<option value="${this.esc(c.id)}" ${draft.clientId === c.id ? "selected" : ""}>${this.esc(c.name)}</option>`).join("")}
                 </select>`}
          </div>
          <div class="fld">
            <label>Projeto *</label>
            <select id="front-project" ${draft.clientId ? "" : "disabled"}>
              <option value="">${draft.clientId ? "Selecione o Projeto…" : "Selecione o Cliente primeiro"}</option>
              ${projects.map(p => `<option value="${this.esc(p.id)}" ${draft.projectId === p.id ? "selected" : ""}>${this.esc(p.code)} — ${this.esc(p.name)}</option>`).join("")}
              ${keepProject}
            </select>
          </div>
          <div class="fld"><label>Código *</label><input id="front-code" value="${this.esc(draft.code)}" placeholder="FS-001" /></div>
          <div class="fld"><label>Nome da Frente *</label><input id="front-name" value="${this.esc(draft.name)}" placeholder="PRELIMINAR U-32" /></div>
          <div class="fld fld-wide"><label>Descrição</label><input id="front-description" value="${this.esc(draft.description ?? "")}" /></div>
          <div class="fld"><label>Data de Início</label><input type="date" id="front-start" value="${this.esc(draft.startDate ?? "")}" /></div>
          <div class="fld"><label>Data de Término</label><input type="date" id="front-end" value="${this.esc(draft.endDate ?? "")}" /></div>
          <div class="fld">
            <label>Status</label>
            <select id="front-status">
              <option value="ACTIVE" ${draft.status !== "INACTIVE" ? "selected" : ""}>ATIVA</option>
              <option value="INACTIVE" ${draft.status === "INACTIVE" ? "selected" : ""}>INATIVA</option>
            </select>
          </div>
          <div class="fld fld-wide"><label>Observações</label><input id="front-notes" value="${this.esc(draft.notes ?? "")}" /></div>
        </div>
        ${this.error ? `<div class="error-box">${this.esc(this.error)}</div>` : ""}
        <div class="form-actions">
          <button type="button" id="btn-cancel-front" class="btn-secondary">Cancelar</button>
          <button type="button" id="btn-save-front" class="btn-primary">Salvar Frente</button>
        </div>
      </div>`;
  }

  render() {
    if (!this.service) {
      this.innerHTML = `<div style="padding:24px; color:var(--color-text-muted);">Serviço de Frentes indisponível nesta sessão.</div>`;
      return;
    }

    const filterProjects = projectsForCascade(this.projects, this.filter, this.authorizedClientIds);

    this.innerHTML = `
      <style>
        .fronts-container { padding: 24px; font-family: Inter, sans-serif; }
        .fronts-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; border-bottom: 1px solid var(--color-border); padding-bottom: 12px; margin-bottom: 18px; flex-wrap: wrap; }
        .fronts-header h1 { margin: 0; font-size: 1.5rem; color: var(--ia-primary); }
        .fronts-header p { margin: 4px 0 0 0; font-size: 0.85rem; color: var(--color-text-muted); }
        .filters { display: flex; gap: 12px; align-items: flex-end; margin-bottom: 18px; flex-wrap: wrap; }
        .fld { display: flex; flex-direction: column; }
        .fld-wide { grid-column: span 2; }
        .fld label { font-size: 0.72rem; color: var(--color-text-muted); font-weight: 600; margin-bottom: 4px; }
        .fld input, .fld select, .filters select { padding: 8px 10px; background: rgba(0,0,0,0.25); border: 1px solid var(--color-graphite-700, var(--color-border)); border-radius: 6px; color: var(--color-text-primary); font-size: 0.86rem; box-sizing: border-box; width: 100%; }
        .hint { font-size: 0.7rem; color: var(--color-text-muted); margin-top: 4px; }
        .btn-primary { padding: 9px 18px; background: var(--ia-action-bg, var(--color-primary)); color: var(--ia-action-fg, var(--color-text-primary)); border: none; border-radius: 6px; cursor: pointer; font-weight: 700; font-size: 0.85rem; }
        .btn-secondary { padding: 9px 18px; background: none; border: 1px solid var(--color-border); color: var(--color-text-muted); border-radius: 6px; cursor: pointer; font-weight: 600; font-size: 0.85rem; }
        .front-form { border: 1px solid var(--color-primary); border-radius: 10px; padding: 18px; margin-bottom: 20px; background: var(--color-surface); }
        .form-title { font-size: 0.78rem; letter-spacing: 0.08em; font-weight: 700; color: var(--ia-primary); margin-bottom: 14px; }
        .form-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; }
        .form-actions { margin-top: 16px; display: flex; gap: 10px; }
        .error-box { margin-top: 14px; padding: 10px 12px; border-radius: 6px; background: #b71c1c; color: #ffebee; font-size: 0.82rem; }
        .data-table { width: 100%; border-collapse: collapse; font-size: 0.83rem; }
        .data-table th, .data-table td { text-align: left; padding: 10px 12px; border-bottom: 1px solid var(--color-border); }
        .data-table th { color: var(--color-text-muted); font-weight: 600; font-size: 0.72rem; }
        .badge { padding: 3px 9px; border-radius: 4px; font-size: 0.7rem; font-weight: 700; }
        .badge-active { background: var(--ia-success-bg, #1b5e20); color: var(--ia-success-fg, #a5d6a7); }
        .badge-inactive { background: var(--color-graphite-700, #333); color: var(--color-graphite-400, #999); }
        .btn-link { background: none; border: none; color: var(--color-text-muted); cursor: pointer; font-size: 0.78rem; padding: 2px 6px; }
        .btn-link:hover { text-decoration: underline; color: var(--ia-primary); }
        .empty-state { text-align: center; padding: 42px 16px; color: var(--color-text-muted); border: 1px dashed var(--color-border); border-radius: 8px; }
        @media (max-width: 900px) { .form-grid { grid-template-columns: 1fr; } .fld-wide { grid-column: span 1; } }
        ${CONTEXT_LOAD_STYLES}
      </style>

      <div class="fronts-container">
        <div class="fronts-header">
          <div>
            <h1>Frentes de Serviço</h1>
            <p>Cada Frente pertence a um Projeto. O Cliente vem pela relação Cliente → Projeto → Frente.</p>
          </div>
          <button type="button" id="btn-new-front" class="btn-primary">+ Nova Frente de Serviço</button>
        </div>

        ${this.ctxLoad.banner()}
        <div class="filters">
          <div class="fld" style="min-width: 260px;">
            <label>Cliente</label>
            <select id="filter-client">
              <option value="">${this.esc(this.ctxLoad.emptyLabel("Clientes", "Nenhum Cliente autorizado", "Todos os Clientes"))}</option>
              ${this.clients.map(c => `<option value="${this.esc(c.id)}" ${this.filter.clientId === c.id ? "selected" : ""}>${this.esc(c.name)}</option>`).join("")}
            </select>
          </div>
          <div class="fld" style="min-width: 260px;">
            <label>Projeto</label>
            <select id="filter-project">
              <option value="">Todos os Projetos</option>
              ${filterProjects.map(p => `<option value="${this.esc(p.id)}" ${this.filter.projectId === p.id ? "selected" : ""}>${this.esc(p.code)} — ${this.esc(p.name)}${this.filter.clientId ? "" : ` (${this.esc(this.clientNameOf(p.clientId))})`}</option>`).join("")}
            </select>
          </div>
          <label style="font-size:0.8rem; color:var(--color-text-muted); display:flex; align-items:center; gap:6px; padding-bottom:9px;">
            <input type="checkbox" id="filter-inactive" ${this.showInactive ? "checked" : ""} style="width:auto;" /> Mostrar inativas
          </label>
        </div>

        ${this.editing ? this.renderForm() : (this.error ? `<div class="error-box">${this.esc(this.error)}</div>` : "")}

        ${this.fronts.length === 0 ? `
          <div class="empty-state">Nenhuma Frente de Serviço cadastrada${this.filter.projectId ? " para o projeto selecionado" : this.filter.clientId ? " para o Cliente selecionado" : ""}.</div>
        ` : `
          <table class="data-table">
            <thead>
              <tr>
                <th>Código</th><th>Frente de Serviço</th><th>Projeto</th><th>Cliente</th>
                <th>Data Inicial</th><th>Data Final</th><th>Apontamentos</th><th>Status</th><th>Ações</th>
              </tr>
            </thead>
            <tbody>
              ${this.fronts.map(front => {
                const usage = this.usageByFront.get(front.id) ?? 0;
                return `
                <tr>
                  <td style="font-family: monospace;">${this.esc(front.code || "—")}</td>
                  <td style="font-weight:600;">${this.esc(front.name)}</td>
                  <td>${this.esc(front.projectName)}</td>
                  <td>${this.esc(front.clientName || this.clientNameOf(String(front.clientId)) || "—")}</td>
                  <td>${this.brDate(front.startDate)}</td>
                  <td>${this.brDate(front.endDate)}</td>
                  <td>${usage}</td>
                  <td><span class="badge ${front.status === "ACTIVE" ? "badge-active" : "badge-inactive"}">${front.status === "ACTIVE" ? "ATIVA" : "INATIVA"}</span></td>
                  <td>
                    <button type="button" class="btn-link btn-edit" data-id="${this.esc(front.id)}">Editar</button>
                    <button type="button" class="btn-link btn-toggle" data-id="${this.esc(front.id)}" data-status="${front.status}">${front.status === "ACTIVE" ? "Inativar" : "Ativar"}</button>
                    <button type="button" class="btn-link btn-delete" data-id="${this.esc(front.id)}" ${usage > 0 ? "disabled title=\"Frente com apontamentos de RDO: inative em vez de excluir.\" style=\"opacity:.4; cursor:not-allowed;\"" : "style=\"color:var(--status-danger-solid);\""}>Excluir</button>
                  </td>
                </tr>`;
              }).join("")}
            </tbody>
          </table>
        `}
      </div>`;

    this.attachEvents();
  }

  private attachEvents() {
    this.querySelector("#btn-new-front")?.addEventListener("click", () => {
      // O formulário começa no recorte do filtro (Cliente e Projeto).
      this.editing = { clientId: this.filter.clientId, projectId: this.filter.projectId, code: "", name: "", status: "ACTIVE" };
      this.error = null;
      this.render();
    });

    // Cascata do filtro: trocar o Cliente limpa o Projeto.
    this.querySelector("#filter-client")?.addEventListener("change", e => {
      this.filter = changeCascade(this.filter, "clientId", (e.target as HTMLSelectElement).value);
      void this.load();
    });
    this.querySelector("#filter-project")?.addEventListener("change", e => {
      this.filter = changeCascade(this.filter, "projectId", (e.target as HTMLSelectElement).value);
      void this.load();
    });
    this.querySelector("#filter-inactive")?.addEventListener("change", e => {
      this.showInactive = (e.target as HTMLInputElement).checked;
      void this.load();
    });

    // Cascata do formulário: trocar o Cliente limpa o Projeto (o digitado é preservado).
    this.querySelector("#front-client")?.addEventListener("change", e => {
      if (!this.editing) return;
      this.captureDraft();
      const next = changeCascade({ ...EMPTY_CASCADE, clientId: this.editing.clientId, projectId: this.editing.projectId }, "clientId", (e.target as HTMLSelectElement).value);
      this.editing = { ...this.editing, clientId: next.clientId, projectId: next.projectId };
      this.render();
    });
    this.querySelector("#front-project")?.addEventListener("change", e => {
      if (this.editing) this.editing = { ...this.editing, projectId: (e.target as HTMLSelectElement).value };
    });

    this.querySelector("#btn-cancel-front")?.addEventListener("click", () => {
      this.editing = null;
      this.error = null;
      this.render();
    });
    this.querySelector("#btn-save-front")?.addEventListener("click", () => void this.save());

    this.querySelectorAll<HTMLElement>(".btn-edit").forEach(btn => {
      btn.addEventListener("click", () => {
        const front = this.fronts.find(f => f.id === btn.dataset["id"]);
        if (!front) return;
        // Edição: Cliente PERSISTIDO da Frente, nunca o da sessão.
        this.editing = {
          id: front.id,
          clientId: String(front.clientId),
          projectId: front.projectId ?? "",
          code: front.code,
          name: front.name,
          description: front.description,
          startDate: front.startDate,
          endDate: front.endDate,
          notes: front.notes,
          status: front.status
        };
        this.error = null;
        this.render();
      });
    });

    this.querySelectorAll<HTMLElement>(".btn-toggle").forEach(btn => {
      btn.addEventListener("click", () => void this.toggleStatus(btn.dataset["id"] ?? "", btn.dataset["status"] ?? ""));
    });
    this.querySelectorAll<HTMLButtonElement>(".btn-delete").forEach(btn => {
      if (btn.disabled) return;
      btn.addEventListener("click", () => void this.removeFront(btn.dataset["id"] ?? ""));
    });
  }
}

if (!customElements.get("app-management-service-fronts-page")) {
  customElements.define("app-management-service-fronts-page", ManagementServiceFrontsPage);
}
